import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  Banknote,
  UploadCloud,
  KeyRound,
  Check,
  Loader2,
  FileText,
  X,
  ArrowRight,
  ArrowLeft,
  AlertCircle,
  MailCheck,
} from "lucide-react";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import receiptService from "../../services/receipt.service";
import accessCodeService from "../../services/accessCode.service";
import { PaymentDetails } from "../../services/admin.service";

interface BookingAccessWizardProps {
  price: number;
  paymentDetails: PaymentDetails | null;
  momoSurchargePercentage?: number;
  onGranted: () => void;
}

const STEPS = [
  { key: "pay", label: "Payment", icon: Banknote },
  { key: "receipt", label: "Receipt", icon: UploadCloud },
  { key: "code", label: "Access Code", icon: KeyRound },
] as const;

const ACCEPTED = "image/jpeg,image/png,image/webp,application/pdf";
const MAX_BYTES = 10 * 1024 * 1024;

const GOLD = "#d4af37";
const NAVY = "#0f1f3d";

function DetailRow({ label, value }: { label: string; value?: string | null }) {
  return (
    <div
      className="flex items-start justify-between gap-4"
      style={{ borderTop: "1px solid rgba(212,175,55,0.18)", paddingTop: 10, paddingBottom: 10 }}
    >
      <span className="text-sm text-gray-600">{label}</span>
      <span className="text-sm font-semibold" style={{ color: NAVY, textAlign: "right" }}>
        {value && value.trim() ? value : "—"}
      </span>
    </div>
  );
}

function Stepper({ current }: { current: number }) {
  return (
    <div className="flex items-center justify-center gap-2 mb-8">
      {STEPS.map((step, i) => {
        const done = i < current;
        const active = i === current;
        const Icon = step.icon;
        return (
          <div key={step.key} className="flex items-center gap-2">
            <div className="flex flex-col items-center gap-2">
              <div
                className="flex items-center justify-center rounded-full"
                style={{
                  width: 40,
                  height: 40,
                  backgroundColor: done || active ? GOLD : "#ffffff",
                  border: `2px solid ${done || active ? GOLD : "rgba(15,31,61,0.18)"}`,
                  color: done || active ? NAVY : "rgba(15,31,61,0.45)",
                  transition: "all 0.3s ease",
                }}
              >
                {done ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
              </div>
              <span
                className="text-xs font-medium"
                style={{ color: active ? NAVY : "rgba(15,31,61,0.5)" }}
              >
                {step.label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div
                className="rounded-full mb-6"
                style={{
                  width: 44,
                  height: 3,
                  backgroundColor: i < current ? GOLD : "rgba(15,31,61,0.12)",
                  transition: "background-color 0.3s ease",
                }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

const stepMotion = {
  initial: { opacity: 0, x: 24 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -24 },
  transition: { duration: 0.32, ease: [0.22, 1, 0.36, 1] as const },
};

export function BookingAccessWizard({ price, paymentDetails, momoSurchargePercentage, onGranted }: BookingAccessWizardProps) {
  const { user, refreshUser } = useAuth();
  const [step, setStep] = useState(0);
  const [resuming, setResuming] = useState(true);

  // Resume from server state: an existing receipt (approval pending or done)
  // means the student is past payment/upload and waiting to enter their code.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const receipt = await receiptService.getMyReceipt();
        // A pending/approved receipt means they're waiting to enter their code.
        // A rejected one means they must upload again — start from the top.
        if (!cancelled && receipt && receipt.status !== "rejected") setStep(2);
      } catch {
        /* fail open: start at step 1 */
      } finally {
        if (!cancelled) setResuming(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (resuming) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: GOLD }} />
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto w-full">
      <Stepper current={step} />
      <AnimatePresence mode="wait">
        {step === 0 && (
          <motion.div key="pay" {...stepMotion}>
            <StepPay
              price={price}
              paymentDetails={paymentDetails}
              momoSurchargePercentage={momoSurchargePercentage}
              onNext={() => setStep(1)}
            />
          </motion.div>
        )}
        {step === 1 && (
          <motion.div key="receipt" {...stepMotion}>
            <StepUpload onBack={() => setStep(0)} onUploaded={() => setStep(2)} />
          </motion.div>
        )}
        {step === 2 && (
          <motion.div key="code" {...stepMotion}>
            <StepAccessCode
              email={user?.email}
              onBack={() => setStep(1)}
              onGranted={async () => {
                await refreshUser();
                onGranted();
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ---------------------------------- Step 1 --------------------------------- */

function StepPay({
  price,
  paymentDetails,
  momoSurchargePercentage,
  onNext,
}: {
  price: number;
  paymentDetails: PaymentDetails | null;
  momoSurchargePercentage?: number;
  onNext: () => void;
}) {
  const hasBank = !!(paymentDetails?.accountNumber || paymentDetails?.accountName || paymentDetails?.bankName);
  const hasMomo = !!paymentDetails?.momoNumber;

  const surchargeRate = momoSurchargePercentage ?? 1;
  const momoFee = Math.round(price * surchargeRate / 100);
  const momoTotal = price + momoFee;

  return (
    <div className="text-center">
      <h3 className="text-xl font-semibold mb-1" style={{ color: NAVY }}>
        Pay your hostel fee
      </h3>
      <p className="text-sm text-gray-600 mb-6">
        Transfer the amount below to the hostel account, then continue to upload your receipt.
      </p>

      <div
        className="rounded-xl p-6 text-left"
        style={{
          background: "linear-gradient(135deg, rgba(212,175,55,0.06), rgba(212,175,55,0.12))",
          border: "1px solid rgba(212,175,55,0.25)",
        }}
      >
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-gray-600">Amount due</span>
          <span className="text-2xl font-bold" style={{ color: NAVY }}>
            GH₵ {price.toLocaleString()}
          </span>
        </div>
        <p className="text-xs text-gray-500 mb-4">Full fee for the academic year, per person.</p>

        {hasBank && (
          <div>
            <DetailRow label="Bank" value={paymentDetails?.bankName} />
            <DetailRow label="Account name" value={paymentDetails?.accountName} />
            <DetailRow label="Account number" value={paymentDetails?.accountNumber} />
            <DetailRow label="Branch" value={paymentDetails?.branch} />
          </div>
        )}

        {hasMomo && (
          <div>
            <DetailRow label="Mobile money number" value={paymentDetails?.momoNumber} />
            <DetailRow label="Mobile money name" value={paymentDetails?.momoName} />
          </div>
        )}

        {!hasBank && !hasMomo && (
          <p className="text-sm text-gray-600" style={{ borderTop: "1px solid rgba(212,175,55,0.18)", paddingTop: 12 }}>
            Payment details are being set up. Please contact the hostel manager for the account to pay into.
          </p>
        )}
      </div>

      {hasMomo && momoFee > 0 && (
        <div
          className="flex items-start gap-3 mt-4 rounded-lg p-4 text-left"
          style={{ backgroundColor: "rgba(212,175,55,0.10)", border: "1px solid rgba(212,175,55,0.35)" }}
        >
          <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: GOLD }} />
          <p className="text-sm" style={{ color: NAVY }}>
            Please note: Mobile Money payments attract a {surchargeRate}% surcharge of GH₵ {momoFee.toLocaleString()},
            so send GH₵ {momoTotal.toLocaleString()} in total — a Mobile Money payment made without this surcharge
            will be marked as invalid.
          </p>
        </div>
      )}

      <div
        className="flex items-start gap-3 mt-4 rounded-lg p-4 text-left"
        style={{ backgroundColor: "rgba(15,31,61,0.04)" }}
      >
        <UploadCloud className="w-5 h-5 mt-1flex-shrink-0" style={{ color: GOLD }} />
        <p className="text-sm text-gray-600">
          On the next step you'll upload your payment receipt for approval — keep it handy
          (a screenshot or PDF works).
        </p>
      </div>

      <Button variant="gold" onClick={onNext} className="w-full py-6 text-base" style={{ marginTop: 24 }}>
        I've paid — continue
        <ArrowRight className="w-4 h-4 ml-2" />
      </Button>
    </div>
  );
}

/* ---------------------------------- Step 2 --------------------------------- */

function StepUpload({ onBack, onUploaded }: { onBack: () => void; onUploaded: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (file && file.type.startsWith("image/")) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setPreviewUrl(null);
  }, [file]);

  const chooseFile = (f: File | null) => {
    setError(null);
    if (!f) return;
    if (!ACCEPTED.split(",").includes(f.type)) {
      setError("Please choose a JPG, PNG, WEBP image or a PDF.");
      return;
    }
    if (f.size > MAX_BYTES) {
      setError("That file is larger than 10 MB. Please choose a smaller one.");
      return;
    }
    setFile(f);
  };

  const handleSubmit = async () => {
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      await receiptService.uploadReceipt(file);
      toast.success("Receipt received — we'll email your access code after review.");
      onUploaded();
    } catch (err: any) {
      const msg = err.message || "Upload failed. Please try again.";
      setError(msg);
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <div className="text-center mb-6">
        <h3 className="text-xl font-semibold mb-1" style={{ color: NAVY }}>
          Upload your payment receipt
        </h3>
        <p className="text-sm text-gray-600">
          We'll review it and email you an access code to confirm your room.
        </p>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED}
        className="hidden"
        onChange={(e) => chooseFile(e.target.files?.[0] || null)}
      />

      {!file ? (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            chooseFile(e.dataTransfer.files?.[0] || null);
          }}
          className="w-full rounded-xl p-8 text-center transition-colors"
          style={{
            border: `2px dashed ${dragOver ? GOLD : "rgba(212,175,55,0.4)"}`,
            backgroundColor: dragOver ? "rgba(212,175,55,0.06)" : "transparent",
            cursor: "pointer",
          }}
        >
          <div
            className="flex items-center justify-center rounded-full mx-auto mb-4"
            style={{ width: 56, height: 56, backgroundColor: "rgba(212,175,55,0.12)" }}
          >
            <UploadCloud className="w-7 h-7" style={{ color: GOLD }} />
          </div>
          <p className="text-sm font-medium" style={{ color: NAVY }}>
            Drag & drop your receipt here
          </p>
          <p className="text-sm text-gray-500 mt-1">
            or <span style={{ color: GOLD, fontWeight: 600 }}>browse to upload</span>
          </p>
          <p className="text-xs text-gray-500 mt-4">JPG, PNG, WEBP or PDF · up to 10 MB</p>
        </button>
      ) : (
        <div
          className="rounded-xl p-4"
          style={{ border: "1px solid rgba(15,31,61,0.12)" }}
        >
          <div className="flex items-center gap-3">
            {previewUrl ? (
              <img
                src={previewUrl}
                alt="Receipt preview"
                className="rounded-lg object-cover flex-shrink-0"
                style={{ width: 56, height: 56 }}
              />
            ) : (
              <div
                className="flex items-center justify-center rounded-lg flex-shrink-0"
                style={{ width: 56, height: 56, backgroundColor: "rgba(15,31,61,0.06)" }}
              >
                <FileText className="w-6 h-6" style={{ color: NAVY }} />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p
                className="text-sm font-medium"
                style={{
                  color: NAVY,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {file.name}
              </p>
              <p className="text-xs text-gray-500 mt-1">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
            </div>
            <button
              type="button"
              onClick={() => setFile(null)}
              disabled={uploading}
              aria-label="Remove file"
              className="flex items-center justify-center rounded-full transition-colors"
              style={{ width: 32, height: 32, color: "rgba(15,31,61,0.55)" }}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {error && (
        <div
          className="flex items-start gap-2 mt-4 rounded-lg p-3"
          style={{ backgroundColor: "#fef2f2", border: "1px solid #fecaca" }}
        >
          <AlertCircle className="w-4 h-4 mt-1flex-shrink-0 text-red-600" />
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      <div className="flex items-start gap-3 mt-4 rounded-lg p-4" style={{ backgroundColor: "rgba(15,31,61,0.04)" }}>
        <MailCheck className="w-5 h-5 mt-1flex-shrink-0" style={{ color: GOLD }} />
        <p className="text-sm text-gray-600">
          After we receive and approve your receipt, a hostel manager will email you an access
          code to confirm ownership of your room.
        </p>
      </div>

      <div className="flex gap-3" style={{ marginTop: 24 }}>
        <Button variant="outline" onClick={onBack} disabled={uploading} className="flex-1 py-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <Button
          variant="gold"
          onClick={handleSubmit}
          disabled={!file || uploading}
          className="flex-1 py-6"
        >
          {uploading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Uploading...
            </>
          ) : (
            <>
              Submit receipt
              <ArrowRight className="w-4 h-4 ml-2" />
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

/* ---------------------------------- Step 3 --------------------------------- */

function StepAccessCode({
  email,
  onBack,
  onGranted,
}: {
  email?: string;
  onBack: () => void;
  onGranted: () => void;
}) {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const formatCode = (value: string) =>
    value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase().slice(0, 6);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!email) {
      setError("Please log in to redeem your access code.");
      return;
    }
    setLoading(true);
    try {
      await accessCodeService.redeemAccessCode({
        email: email.trim().toLowerCase(),
        code: code.trim().toUpperCase(),
      });
      toast.success("Access code accepted! You can now book your room.");
      onGranted();
    } catch (err: any) {
      const msg = err.message || "Failed to redeem access code. Please try again.";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="text-center">
      <div
        className="flex items-center justify-center rounded-full mx-auto mb-4"
        style={{ width: 56, height: 56, backgroundColor: "rgba(212,175,55,0.12)" }}
      >
        <KeyRound className="w-7 h-7" style={{ color: GOLD }} />
      </div>
      <h3 className="text-xl font-semibold mb-1" style={{ color: NAVY }}>
        Enter your access code
      </h3>
      <p className="text-sm text-gray-600 mb-6 max-w-md mx-auto">
        Once your receipt is approved, we email you a 6-character code. Enter it below to confirm
        ownership of your room.
      </p>

      <input
        value={code}
        onChange={(e) => setCode(formatCode(e.target.value))}
        placeholder="ABC123"
        inputMode="text"
        autoComplete="one-time-code"
        className="w-full rounded-lg font-mono text-xl"
        style={{
          height: 56,
          textAlign: "center",
          letterSpacing: "0.4em",
          textTransform: "uppercase",
          border: "1px solid rgba(15,31,61,0.18)",
          backgroundColor: "#f3f3f5",
        }}
      />

      {error && (
        <div
          className="flex items-start gap-2 mt-4 rounded-lg p-3 text-left"
          style={{ backgroundColor: "#fef2f2", border: "1px solid #fecaca" }}
        >
          <AlertCircle className="w-4 h-4 mt-1flex-shrink-0 text-red-600" />
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      <p className="text-xs text-gray-500 mt-4">
        Haven't received it yet? Approval can take up to 24 hours after you upload your receipt.
      </p>

      <div className="flex gap-3" style={{ marginTop: 24 }}>
        <Button type="button" variant="outline" onClick={onBack} disabled={loading} className="flex-1 py-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <Button type="submit" variant="gold" disabled={code.length !== 6 || loading} className="flex-1 py-6">
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Verifying...
            </>
          ) : (
            "Confirm my room"
          )}
        </Button>
      </div>
    </form>
  );
}
